import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCompanyAnalytics } from '@/hooks/useCompanyAnalytics';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { ChartContainer } from '@/components/ui/chart';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { CaregiverRankingTable } from './CaregiverRankingTable';
import { AttentionList } from './AttentionList';
import { PatientSummaryTable } from './PatientSummaryTable';
import { ClipboardCheck, Clock, AlertTriangle, TrendingUp, HeartPulse } from 'lucide-react';

interface Props {
  companyId: string;
}

const PERIOD_OPTIONS = [7, 15, 30] as const;

export function CompanyAnalytics({ companyId }: Props) {
  const { t } = useTranslation();
  const [period, setPeriod] = useState<number>(7);
  const { data, isLoading } = useCompanyAnalytics(companyId, period);

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-24" />)}
        </div>
        <Skeleton className="h-64" />
        <Skeleton className="h-48" />
      </div>
    );
  }

  if (!data) return null;

  const chartConfig = {
    on_time: { label: t('analytics.onTime'), color: 'hsl(var(--primary))' },
    late: { label: t('analytics.late'), color: 'hsl(0 84% 60%)' },
  };

  const chartData = data.daily_activity.map(d => ({
    ...d,
    label: d.date.slice(5), // MM-DD
  }));

  return (
    <div className="space-y-5">
      {/* Period filter */}
      <div className="overflow-x-auto -mx-4 px-4 scrollbar-none">
        <div className="flex gap-2 w-max">
          {PERIOD_OPTIONS.map((p) => (
            <Button
              key={p}
              size="sm"
              variant={period === p ? 'default' : 'outline'}
              onClick={() => setPeriod(p)}
              className="whitespace-nowrap"
            >
              {t('analytics.lastNDays', { count: p })}
            </Button>
          ))}
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
              <ClipboardCheck className="h-5 w-5 text-primary" />
            </div>
            <div>
              <p className="text-2xl font-bold">{data.kpis.total_tasks}</p>
              <p className="text-xs text-muted-foreground">{t('analytics.totalTasks')}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-success/10 flex items-center justify-center">
              <TrendingUp className="h-5 w-5 text-success" />
            </div>
            <div>
              <p className="text-2xl font-bold text-success">{data.kpis.on_time_pct}%</p>
              <p className="text-xs text-muted-foreground">{t('analytics.punctuality')}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-warning/10 flex items-center justify-center">
              <Clock className="h-5 w-5 text-warning" />
            </div>
            <div>
              <p className="text-2xl font-bold text-warning">{data.kpis.late_count}</p>
              <p className="text-xs text-muted-foreground">{t('analytics.lateTasks')}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-destructive/10 flex items-center justify-center">
              <AlertTriangle className="h-5 w-5 text-destructive" />
            </div>
            <div>
              <p className="text-2xl font-bold text-destructive">{data.kpis.incidents}</p>
              <p className="text-xs text-muted-foreground">{t('analytics.incidents')}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-destructive/10 flex items-center justify-center">
              <HeartPulse className="h-5 w-5 text-destructive" />
            </div>
            <div>
              <p className="text-2xl font-bold text-destructive">{data.kpis.vital_alerts}</p>
              <p className="text-xs text-muted-foreground">{t('health.vitalAlerts')}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Daily Activity Chart */}
      {chartData.length > 0 && (
        <Card>
          <CardContent className="p-3">
            <p className="text-sm font-semibold mb-3">{t('analytics.dailyActivity')}</p>
            <ChartContainer config={chartConfig} className="h-64 w-full">
              <BarChart data={chartData} margin={{ top: 8, right: 8, left: -8, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                <XAxis dataKey="label" tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }} tickMargin={4} tickLine={false} axisLine={false} />
                <YAxis width={40} allowDecimals={false} tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }} tickLine={false} axisLine={false} />
                <Tooltip contentStyle={{ backgroundColor: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: '8px', color: 'hsl(var(--foreground))' }} />
                <Legend />
                <Bar dataKey="on_time" name={t('analytics.onTime')} stackId="a" fill="hsl(var(--primary))" radius={[0, 0, 0, 0]} />
                <Bar dataKey="late" name={t('analytics.late')} stackId="a" fill="hsl(0 84% 60%)" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ChartContainer>
          </CardContent>
        </Card>
      )}

      {/* Attention List */}
      <AttentionList items={data.attention_list} />

      {/* Caregiver Ranking */}
      <CaregiverRankingTable ranking={data.caregiver_ranking} />

      {/* Patient Summary */}
      <PatientSummaryTable patients={data.patient_summary} />
    </div>
  );
}
